import type {
  FaceKey,
  PlacementPosition,
  ResolvedEditAreaDefinition,
  ShapedComponent,
  Surface,
} from '@repo/api-contracts';
import { componentRectOnPhoto, type PhotoDimensions, type PhotoRect } from '@repo/design-renderer';
import { warpedRectOnPhoto } from './warped-view';

/**
 * 한 면에 놓인 인쇄 영역 하나 — 컴포넌트(mm)와 공유 공간 위 자리, 그리고 놓인 표면·위치(휜 편집이 좌표를 옮길 때 쓴다).
 * 곡면(cylinder·mesh) 자리의 rect 는 휜 외곽의 외접 상자다.
 */
export interface SurfaceSlot {
  readonly component: ShapedComponent & { readonly id: string; readonly name: string };
  readonly rect: PhotoRect;
  readonly surface: Surface;
  readonly position: PlacementPosition;
}

/**
 * 한 면의 편집 표면 전부. 목업 사진이 있으면 공간은 사진(px)이고 자리는 사진 위 인쇄 영역이다.
 * 사진이 없으면 인쇄 영역들을 mm 단위 가상 공간에 가로로 나란히 둔다.
 */
export interface FaceSurfaces {
  readonly backdrop: { readonly url: string } | null;
  readonly space: PhotoDimensions;
  readonly slots: readonly SurfaceSlot[];
}

interface FaceSurfacesInput {
  readonly definition: ResolvedEditAreaDefinition;
  readonly colorOptionId: string;
  readonly faceKey: FaceKey;
}

/** 사진이 없을 때 인쇄 영역 사이 간격(mm). */
const VIRTUAL_GAP_MM = 10;

/**
 * 면에 배치된 인쇄 영역 전부를 한 공간에 모은다 — 캔버스가 한 번에 보여 주고 어느 영역이든 바로 편집하게.
 * 사진·배치·컴포넌트가 하나라도 빠진 배치는 건너뛴다.
 */
export const faceSurfacesOf = ({
  definition,
  colorOptionId,
  faceKey,
}: FaceSurfacesInput): FaceSurfaces => {
  const face = definition.faces[faceKey];
  const photo = definition.colors.find((entry) => entry.colorOptionId === colorOptionId)?.faces[
    faceKey
  ];
  const placed = (face?.placements ?? []).flatMap((placement) => {
    const component = definition.components.find((c) => c.id === placement.componentId);
    const surface = face?.surfaces.find((s) => s.id === placement.surfaceId);
    return component === undefined || surface === undefined
      ? []
      : [{ placement, component, surface }];
  });

  if (photo !== undefined) {
    return {
      backdrop: { url: photo.photoUrl },
      space: photo.photoSize,
      slots: placed.map(({ placement, component, surface }) => ({
        component,
        rect:
          surface.type === 'flat'
            ? componentRectOnPhoto(surface, photo.photoSize, component, placement.position)
            : warpedRectOnPhoto(
                { component, surface, position: placement.position },
                photo.photoSize,
              ),
        surface,
        position: placement.position,
      })),
    };
  }

  // 사진 없음 — mm 를 그대로 공간 단위로 쓰고 가로로 늘어놓는다.
  let cursor = 0;
  const slots = placed.map(({ placement, component, surface }) => {
    const rect = { x: cursor, y: 0, width: component.widthMm, height: component.heightMm };
    cursor += component.widthMm + VIRTUAL_GAP_MM;
    return { component, rect, surface, position: placement.position };
  });
  return {
    backdrop: null,
    space: {
      widthPx: Math.max(1, cursor - VIRTUAL_GAP_MM),
      heightPx: Math.max(1, ...slots.map((slot) => slot.rect.height)),
    },
    slots,
  };
};

/** 자리들의 외접 사각형 — 캔버스가 area 모드에서 이것을 화면에 채운다. 비어 있으면 null. */
export const unionRectOf = (rects: readonly PhotoRect[]): PhotoRect | null => {
  const first = rects[0];
  if (first === undefined) return null;
  let left = first.x;
  let top = first.y;
  let right = first.x + first.width;
  let bottom = first.y + first.height;
  for (const rect of rects.slice(1)) {
    left = Math.min(left, rect.x);
    top = Math.min(top, rect.y);
    right = Math.max(right, rect.x + rect.width);
    bottom = Math.max(bottom, rect.y + rect.height);
  }
  return { x: left, y: top, width: right - left, height: bottom - top };
};
